import { t } from '../i18n';
import { useRef, useState } from 'react';

import type { CellValue, Property, PropertyOption } from '@noti/shared';

import { asText, cellValue, CHIP_COLOR, formatStamp, TYPE_LABEL } from './database-rows';
import type { DatabaseRow } from './database-rows';
import { ActionPopover } from './ActionPopover';
import { Dropdown, optionToDropdown } from './DatabaseDropdown';
import { Glyph } from './icons';

/**
 * One table cell. Which editor a property gets follows the prototype's cell
 * macro; the geometry lives in `dbcell__*` in workspace.css.
 */

export interface DatabaseCellProps {
  row: DatabaseRow;
  relationOptions?: Record<string, { id: string; title: string }[]>;
  onOpenRow?: (id: string) => void;
  property: Property;
  people: { id: string; name: string }[];
  onWrite: (rowId: string, property: Property, value: CellValue) => void;
  onCreateOption: (property: Property, name: string) => void;
  onRenameOption: (property: Property, value: string, name: string) => void;
  onRecolorOption: (property: Property, value: string, color: PropertyOption['c']) => void;
  onRemoveOption: (property: Property, value: string) => void;
}

const TEXT_TYPES = new Set(['title', 'text', 'url', 'email', 'phone']);

export function DatabaseCell(props: DatabaseCellProps): React.ReactElement {
  const { row, property } = props;
  const value = cellValue(row, property);

  if (property.type === 'created' || property.type === 'edited') {
    return <span className="dbcell__static">{formatStamp(value)}</span>;
  }
  if (property.type === 'checkbox') {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={value === true}
        aria-label={property.name}
        className={`dbcell__box${value === true ? ' is-on' : ''}`}
        onClick={() => props.onWrite(row.id, property, value === true ? '' : true)}
      >
        {value === true ? <Glyph d="m5 12 5 5 9-10" size={14} strokeWidth={3} /> : null}
      </button>
    );
  }
  if (property.type === 'select' || property.type === 'status') {
    return (
      <Dropdown
        exclusive
        className="dbcell__dd"
        noChevron
        label={property.name}
        value={asText(value)}
        options={[{ value: '', label: '—' }, ...optionToDropdown(property.options)]}
        onPick={(next) => props.onWrite(row.id, property, next)}
        onCreate={(name) => props.onCreateOption(property, name)}
        onRename={(value, name) => props.onRenameOption(property, value, name)}
        onRecolor={(value, color) => props.onRecolorOption(property, value, color)}
        onRemove={(value) => props.onRemoveOption(property, value)}
      />
    );
  }
  if (property.type === 'person' || property.type === 'relation') {
    const choices =
      property.type === 'person'
        ? props.people.map((person) => ({ value: person.id, label: person.name }))
        : (props.relationOptions?.[property.id] ?? []).map((row) => ({
            value: row.id,
            label: row.title || t('database.untitled'),
          }));
    const options = choices.map((choice) => ({ n: choice.value, c: 'grey' as const }));
    const selected = Array.isArray(value)
      ? value
      : typeof value === 'string' && value
        ? [value]
        : [];
    return (
      <MultiCell
        value={selected}
        property={{ ...property, options }}
        choices={choices}
        onWrite={(next) => props.onWrite(row.id, property, next)}
        onOpen={property.type === 'relation' ? props.onOpenRow : undefined}
      />
    );
  }
  if (property.type === 'rollup') return <span className="dbcell__static">{asText(value)}</span>;
  if (property.type === 'multi') {
    return (
      <MultiCell
        value={value}
        property={property}
        onWrite={(next) => props.onWrite(row.id, property, next)}
        onCreate={(name) => props.onCreateOption(property, name)}
      />
    );
  }
  if (property.type === 'date') {
    return (
      <DateCell
        value={asText(value)}
        property={property}
        onWrite={(next) => props.onWrite(row.id, property, next)}
      />
    );
  }
  if (property.type === 'number') {
    return (
      <input
        className="cin dbcell__input"
        type="number"
        value={typeof value === 'number' ? value : ''}
        aria-label={property.name}
        onChange={(event) => {
          const raw = event.target.value;
          props.onWrite(row.id, property, raw === '' ? '' : Number(raw));
        }}
      />
    );
  }
  if (TEXT_TYPES.has(property.type)) {
    return (
      <TextCell
        value={asText(value)}
        placeholder={property.type === 'title' ? t('database.untitled') : t('database.empty')}
        label={property.name}
        weight={property.type === 'title' ? 500 : undefined}
        onChange={(next) => props.onWrite(row.id, property, next)}
      />
    );
  }
  return <span className="dbcell__static">{asText(value) || TYPE_LABEL[property.type]}</span>;
}

/** Keep rows compact; the full value is editable in a floating field. */
function TextCell({
  value,
  placeholder,
  label,
  weight,
  onChange,
}: {
  value: string;
  placeholder: string;
  label: string;
  weight?: number;
  onChange: (value: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [editorOffset, setEditorOffset] = useState(0);
  const changeOpen = (next: boolean) => {
    if (next) setEditorOffset(-(triggerRef.current?.getBoundingClientRect().height ?? 0));
    setOpen(next);
  };

  return (
    <ActionPopover
      exclusive
      open={open}
      onOpenChange={changeOpen}
      sideOffset={editorOffset}
      label={label}
      className="dbcell__textpop"
      rootClassName="dbcell__textcell"
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        const panel = event.currentTarget;
        if (!(panel instanceof HTMLElement)) return;
        panel.querySelector('textarea')?.focus({ preventScroll: true });
      }}
      trigger={
        <button
          type="button"
          ref={triggerRef}
          className={`cin dbcell__textbtn${value ? '' : ' is-empty'}`}
          aria-label={label}
          style={weight ? { fontWeight: weight } : undefined}
        >
          {value || placeholder}
        </button>
      }
    >
      <textarea
        ref={(field) => {
          if (!field) return;
          const fit = (): void => {
            field.style.height = 'auto';
            field.style.height = `${field.scrollHeight}px`;
          };
          fit();
          const observer = new ResizeObserver(fit);
          observer.observe(field);
          return () => observer.disconnect();
        }}
        className="dbcell__texteditor"
        rows={1}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        style={weight ? { fontWeight: weight } : undefined}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
          event.preventDefault();
          setOpen(false);
        }}
      />
    </ActionPopover>
  );
}

function MultiCell({
  value,
  property,
  onWrite,
  onCreate,
  choices,
  onOpen,
}: {
  choices?: { value: string; label: string }[];
  onOpen?: (id: string) => void;
  value: CellValue;
  property: Property;
  onWrite: (next: string[]) => void;
  onCreate?: (name: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = Array.isArray(value) ? value : [];

  // Typing filters the options; an unknown name becomes a new option, the same
  // rule the single-choice dropdown uses.
  const needle = query.trim().toLowerCase();
  const labelOf = (value: string): string =>
    choices?.find((choice) => choice.value === value)?.label ?? value;
  const shown = needle
    ? property.options.filter((option) => labelOf(option.n).toLowerCase().includes(needle))
    : property.options;
  const exact = property.options.some((option) => option.n.toLowerCase() === needle);
  const canCreate = Boolean(onCreate) && needle !== '' && !exact;

  const changeOpen = (next: boolean) => {
    if (next) setQuery('');
    setOpen(next);
  };

  const toggle = (name: string) =>
    onWrite(
      selected.includes(name) ? selected.filter((item) => item !== name) : [...selected, name],
    );

  // The options form one vertical list; Tab should not walk through all of them.
  const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const stops = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]')];
    const at = stops.indexOf(event.target as HTMLElement);
    if (at < 0) return;
    let next: HTMLElement | undefined;
    if (event.key === 'ArrowDown') next = stops[at + 1];
    else if (event.key === 'ArrowUp') next = stops[at - 1];
    else if (event.key === 'Home') next = stops[0];
    else if (event.key === 'End') next = stops[stops.length - 1];
    else return;
    event.preventDefault();
    next?.focus();
  };

  return (
    <ActionPopover
      exclusive
      open={open}
      onOpenChange={changeOpen}
      label={property.name}
      className="dbcell__multipop"
      rootClassName="dbcell__multi"
      onOpenAutoFocus={(event) => {
        const panel = event.currentTarget;
        if (!(panel instanceof HTMLElement)) return;
        const target =
          panel.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ??
          panel.querySelector<HTMLElement>('[role="option"]');
        if (!target) return;
        event.preventDefault();
        target.focus();
      }}
      trigger={
        <button
          type="button"
          className={`cin dbcell__multibtn${open ? ' is-open' : ''}`}
          aria-haspopup="listbox"
          aria-label={property.name}
        >
          {selected.length === 0
            ? null
            : selected.map((name) => {
                const option = property.options.find((item) => item.n === name);
                return (
                  <span
                    key={name}
                    className="chip"
                    style={{
                      background: CHIP_COLOR[option?.c ?? 'grey'].bg,
                      color: CHIP_COLOR[option?.c ?? 'grey'].fg,
                    }}
                  >
                    {labelOf(name)}
                  </span>
                );
              })}
        </button>
      }
    >
      {onOpen
        ? selected.map((id) => (
            <button key={id} type="button" className="dbcell__related" onClick={() => onOpen(id)}>
              {t('database.open')}: {labelOf(id)}
            </button>
          ))
        : null}
      <div
        className="dbcell__multilist"
        role="listbox"
        aria-label={property.name}
        onKeyDown={moveFocus}
      >
        {shown.map((option) => {
          const on = selected.includes(option.n);
          return (
            <button
              key={option.n}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={on}
              className="dbcell__multirow"
              onClick={() => toggle(option.n)}
            >
              <span className={`dbcell__minibox${on ? ' is-on' : ''}`}>
                {on ? <Glyph d="m5 12 5 5 9-10" size={12} strokeWidth={3} /> : null}
              </span>
              <span
                className="chip"
                style={{
                  background: CHIP_COLOR[option.c].bg,
                  color: CHIP_COLOR[option.c].fg,
                }}
              >
                {labelOf(option.n)}
              </span>
            </button>
          );
        })}
        {canCreate ? (
          <button
            type="button"
            className="dd__create"
            onClick={() => {
              onCreate?.(query.trim());
              setQuery('');
            }}
          >
            {t('database.createOption', { name: query.trim() })}
          </button>
        ) : null}
        {shown.length === 0 && !canCreate ? (
          <span className="dd__empty">{t('database.noOptions')}</span>
        ) : null}
      </div>
      <input
        className="ni dd__search"
        value={query}
        placeholder={t('database.searchOrCreate')}
        aria-label={t('database.searchOptions')}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          // One match left: the user is looking for that option, not for a new one.
          const only = shown.length === 1 ? shown[0] : undefined;
          if (only) {
            toggle(only.n);
            setQuery('');
            return;
          }
          if (canCreate) {
            onCreate?.(query.trim());
            setQuery('');
          }
        }}
      />
    </ActionPopover>
  );
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

function isoOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

function daysInMonth(month: number, year: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Where the cursor lands, both in the grid and after a keyboard move. */
interface DaySpot {
  month: number;
  year: number;
  day: number;
}

function spotOn(iso: string): DaySpot | null {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return { month: parsed.getMonth(), year: parsed.getFullYear(), day: parsed.getDate() };
}

/** Move by whole days; `Date` normalises the overflow, so month ends land correctly. */
function shiftDays(spot: DaySpot, delta: number): DaySpot {
  const moved = new Date(spot.year, spot.month, spot.day + delta);
  return { month: moved.getMonth(), year: moved.getFullYear(), day: moved.getDate() };
}

/** Move by whole months, keeping the day number where the month is long enough. */
function shiftMonths(spot: DaySpot, delta: number): DaySpot {
  const moved = new Date(spot.year, spot.month + delta, 1);
  const year = moved.getFullYear();
  const month = moved.getMonth();
  return { month, year, day: Math.min(spot.day, daysInMonth(month, year)) };
}

function formatDay(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Monday-first grid of the given month, padded to whole weeks. */
function monthGrid(year: number, month: number): { iso: string; day: number; outside: boolean }[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  const cells: { iso: string; day: number; outside: boolean }[] = [];
  for (let index = 0; index < 42; index += 1) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    cells.push({
      iso: isoOf(day),
      day: day.getDate(),
      outside: day.getMonth() !== month,
    });
  }
  return cells;
}

function DateCell({
  value,
  property,
  onWrite,
}: {
  value: string;
  property: Property;
  onWrite: (next: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  // The grid opens on the stored date, or on today when the cell is empty.
  const initial = spotOn(value) ??
    spotOn(isoOf(new Date())) ?? {
      month: 0,
      year: 1970,
      day: 1,
    };
  const [spot, setSpot] = useState<DaySpot>(initial);
  const stored = spotOn(value) ?? spotOn(isoOf(new Date()));

  const changeOpen = (next: boolean) => {
    // Reopening always starts from the stored date, not from the month last browsed.
    if (next && stored) setSpot(stored);
    setOpen(next);
  };

  // One day carries the tab stop, so Tab does not walk the whole grid. The stop takes the
  // focus as it mounts, since Radix mounts the panel after this component's effects run.
  const cursor = isoOf(
    new Date(spot.year, spot.month, Math.min(spot.day, daysInMonth(spot.month, spot.year))),
  );
  const stopRef = (node: HTMLButtonElement | null) => {
    node?.focus();
  };

  const moveCursor = (event: React.KeyboardEvent<HTMLDivElement>) => {
    let next: DaySpot | undefined;
    if (event.key === 'ArrowLeft') next = shiftDays(spot, -1);
    else if (event.key === 'ArrowRight') next = shiftDays(spot, 1);
    else if (event.key === 'ArrowUp') next = shiftDays(spot, -7);
    else if (event.key === 'ArrowDown') next = shiftDays(spot, 7);
    else if (event.key === 'PageUp') next = shiftMonths(spot, -1);
    else if (event.key === 'PageDown') next = shiftMonths(spot, 1);
    else if (event.key === 'Home') next = { ...spot, day: 1 };
    else if (event.key === 'End') next = { ...spot, day: daysInMonth(spot.month, spot.year) };
    else return;
    event.preventDefault();
    setSpot(next);
  };

  const today = new Date();
  const todayIso = isoOf(today);

  return (
    <ActionPopover
      exclusive
      open={open}
      onOpenChange={changeOpen}
      label={t('database.chooseDate')}
      className="dbcell__datepop"
      rootClassName="dbcell__date"
      // The grid focuses its own stop below, so Radix must not take the focus itself.
      onOpenAutoFocus={(event) => event.preventDefault()}
      trigger={
        <button
          type="button"
          className={`cin dbcell__datebtn${open ? ' is-open' : ''}`}
          aria-haspopup="dialog"
          aria-label={property.name}
        >
          <Glyph d="M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4" size={14} />
          <span className={value ? undefined : 'is-empty'}>
            {value ? formatDay(value) : t('database.empty')}
          </span>
        </button>
      }
    >
      <div className="dbcell__datetop">
        <button
          type="button"
          className="dbcell__datenav"
          aria-label={t('database.previousMonth')}
          onClick={() => setSpot(shiftMonths(spot, -1))}
        >
          ‹
        </button>
        <span>
          {new Date(spot.year, spot.month, 1).toLocaleDateString('en-GB', {
            month: 'long',
            year: 'numeric',
          })}
        </span>
        <button
          type="button"
          className="dbcell__datenav"
          aria-label={t('database.nextMonth')}
          onClick={() => setSpot(shiftMonths(spot, 1))}
        >
          ›
        </button>
      </div>
      <div className="dbcell__weekdays">
        {WEEKDAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="dbcell__daygrid" onKeyDown={moveCursor}>
        {monthGrid(spot.year, spot.month).map((cell) => (
          <button
            key={cell.iso}
            type="button"
            data-stop={cell.iso === cursor}
            tabIndex={cell.iso === cursor ? 0 : -1}
            ref={cell.iso === cursor ? stopRef : undefined}
            className={`dbcell__day${cell.iso === value ? ' is-selected' : ''}${
              cell.iso === todayIso ? ' is-today' : ''
            }${cell.outside ? ' is-outside' : ''}`}
            aria-label={cell.iso}
            onClick={() => {
              onWrite(cell.iso);
              setOpen(false);
            }}
          >
            {cell.day}
          </button>
        ))}
      </div>
      <div className="dbcell__datefoot">
        <button
          type="button"
          className="dbcell__today"
          onClick={() => {
            onWrite(todayIso);
            setOpen(false);
          }}
        >
          {t('database.today')}
        </button>
        <button
          type="button"
          className="dbcell__clear"
          onClick={() => {
            onWrite('');
            setOpen(false);
          }}
        >
          {t('database.clear')}
        </button>
      </div>
    </ActionPopover>
  );
}
